
/**
 * Theme Webhook Interceptor
 * 
 * This script intercepts cart and order events in the theme
 * and sends them to your webhook endpoint for cart hold management.
 * 
 * Configuration: Update the WEBHOOK_CONFIG object below
 */

// ============================================================================
// SAVE ORIGINAL FETCH BEFORE ANY INTERCEPTION
// ============================================================================

// CRITICAL: Save the original fetch function BEFORE any code runs
// This must be the FIRST thing we do to avoid interception loops
// Use an IIFE to ensure we capture it immediately
const originalFetchGlobal = (function() {
  // Get the native fetch before anything can intercept it
  const nativeFetch = window.fetch;
  // Store it with proper binding
  return function(url, options) {
    return nativeFetch.call(window, url, options);
  };
})();

// ============================================================================
// WEBHOOK DEDUPLICATION
// ============================================================================

// Track recently sent webhooks to prevent duplicates
const recentWebhooks = new Map();
const WEBHOOK_DEDUP_WINDOW = 2000; // 2 seconds - don't send same webhook twice within this window

function getWebhookKey(eventType, variantId, cartToken) {
  // Create a unique key for deduplication
  // For carts/create and carts/remove events, use longer window (5 seconds) to prevent duplicates
  // For other events, use shorter window (2 seconds)
  const isLongWindow = eventType === 'carts/create' || eventType === 'create' || 
                       eventType === 'carts/remove' || eventType === 'remove';
  const windowSize = isLongWindow ? 5000 : WEBHOOK_DEDUP_WINDOW; // 5 seconds for create/remove, 2 for others
  const timestamp = Math.floor(Date.now() / windowSize); // Round to window size
  
  if (eventType === 'carts/create' || eventType === 'create') {
    // For create events, only use variant ID + time window (ignore cart token)
    return `${eventType}:${variantId || 'none'}:${timestamp}`;
  } else if (eventType === 'carts/remove' || eventType === 'remove') {
    // For remove events, use variant ID + cart token + time window (but longer window to catch rapid duplicates)
    return `${eventType}:${variantId || 'none'}:${cartToken || 'none'}:${timestamp}`;
  } else {
    // For other events, include cart token
    return `${eventType}:${variantId || 'none'}:${cartToken || 'none'}:${timestamp}`;
  }
}

function shouldSendWebhook(eventType, variantId, cartToken) {
  const key = getWebhookKey(eventType, variantId, cartToken);
  
  if (recentWebhooks.has(key)) {
    logWebhookEvent('webhook_dedup', { eventType, variantId, reason: 'Duplicate within dedup window' });
    return false;
  }
  
  // Mark as sent
  recentWebhooks.set(key, Date.now());
  
  // Clean up old entries (older than 10 seconds)
  setTimeout(() => {
    recentWebhooks.delete(key);
  }, 10000);
  
  return true;
}

// ============================================================================
// CONFIGURATION - UPDATE THESE VALUES
// ============================================================================

const WEBHOOK_CONFIG = {
  // Your webhook endpoint URL
  webhookUrl: 'https://shopify.infinitediscs.com/webhook/theme_webhook.php',
  
  // Your shop domain (e.g., 'your-shop.myshopify.com')
  shop: 'disc-source.myshopify.com',
  apiKey: 'c0ade8be7fe6165b9fa0d8eb0779efb7bb9eafe9e65914794d70c52bc8c34a6f',
  
  // Enable/disable webhook sending
  enabled: true,
  
  // Enable debug logging (set to false in production)
  debug: true,
  
  // Cart hold expiration time in minutes
  cartHoldExpiration: 30,
  
  // Events to track
  events: {
    cart: {
      add: true,      // Track add to cart
      update: true,   // Track quantity updates
      remove: true,   // Track item removal
      clear: true     // Track cart clearing
    },
    order: {
      create: true,   // Track order creation
      update: true,   // Track order updates
      cancel: true    // Track order cancellation
    }
  }
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

// Log webhook events
function logWebhookEvent(event, data) {
  const config = window.DAWN_WEBHOOK_CONFIG || WEBHOOK_CONFIG;
  console.log(`[Theme Webhook] logWebhookEvent called:`, { event, config, debug: config.debug });
  if (config.debug) {
    console.log(`[Theme Webhook] ${event}:`, data);
  } else {
    console.log(`[Theme Webhook] Debug disabled, not logging:`, event);
  }
}

// Send data to webhook endpoint
async function sendWebhookEvent(eventType, eventData, additionalData = {}) {
  // Use global config if available, otherwise fall back to local config
  const config = window.DAWN_WEBHOOK_CONFIG || WEBHOOK_CONFIG;
  
  console.log('[Theme Webhook] sendWebhookEvent called with:', { eventType, eventData, config });
  
  if (!config.enabled) {
    logWebhookEvent('disabled', `Webhook sending is disabled for ${eventType}`);
    return { success: false, message: 'Webhook disabled' };
  }

  try {
    console.log('[Theme Webhook] Creating payload...');
    const payload = {
      event_type: eventType,
      shop: config.shop,
      timestamp: new Date().toISOString(),
      theme: 'custom',
      data: eventData,
      ...additionalData
    };

    console.log('[Theme Webhook] Payload created:', payload);
    console.log('[Theme Webhook] About to send webhook to:', config.webhookUrl);
    logWebhookEvent('sending', payload);

    const headers = {
      'content-type': 'application/json',
      'x-shopify-topic': eventType,
      'x-shopify-shop-domain': config.shop,
      'x-shopify-hmac-sha256': '', // Empty for theme events (no HMAC from Shopify)
      'x-api-key': config.apiKey || '', // Include API key if available
      // Note: 'user-agent' header cannot be set by JavaScript (browser security)
      'x-webhook-event': eventType,
      'x-shopify-theme': 'Custom',
      'x-custom-source': 'Custom-Theme',
      ...config.customHeaders
    };

    console.log('[Theme Webhook] Sending webhook via XMLHttpRequest (more reliable for CORS)...');
    
    // Use XMLHttpRequest instead of fetch for better reliability and CORS support
    const response = await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', config.webhookUrl, true);
      
      // Set all headers
      Object.keys(headers).forEach(key => {
        xhr.setRequestHeader(key, headers[key]);
      });
      
      xhr.onload = function() {
        // Create a fetch-like response object
        resolve({
          ok: xhr.status >= 200 && xhr.status < 300,
          status: xhr.status,
          statusText: xhr.statusText,
          text: () => Promise.resolve(xhr.responseText),
          json: () => Promise.resolve(JSON.parse(xhr.responseText || '{}'))
        });
      };
      
      xhr.onerror = function() {
        reject(new Error(`XMLHttpRequest failed: ${xhr.statusText || 'Network error'}`));
      };
      
      xhr.ontimeout = function() {
        reject(new Error('XMLHttpRequest timeout'));
      };
      
      xhr.timeout = 30000; // 30 second timeout
      xhr.send(JSON.stringify(payload));
    });

    console.log('[Theme Webhook] Fetch response received:', { status: response.status, ok: response.ok });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[Theme Webhook] HTTP error response:', errorText);
      throw new Error(`HTTP error! status: ${response.status}, response: ${errorText}`);
    }

    const result = await response.json().catch(() => ({ success: true }));
    console.log('[Theme Webhook] Webhook success:', { eventType, status: response.status, result });
    logWebhookEvent('success', { eventType, status: response.status, result });
    
    return { success: true, result };
  } catch (error) {
    console.error('[Theme Webhook] Webhook error:', { eventType, error: error.message, stack: error.stack });
    logWebhookEvent('error', { eventType, error: error.message });
    return { success: false, error: error.message };
  }
}

// Extract cart data from Shopify cart response
function extractCartData(cartResponse) {
  if (!cartResponse) return null;

  return {
    cart_token: cartResponse.token || null,
    item_count: cartResponse.item_count || 0,
    total_price: cartResponse.total_price || 0,
    total_weight: cartResponse.total_weight || 0,
    currency: cartResponse.currency || 'USD',
    items: (cartResponse.items || []).map(item => ({
      id: item.id,
      variant_id: item.variant_id,
      product_id: item.product_id,
      quantity: item.quantity,
      price: item.price,
      title: item.title,
      variant_title: item.variant_title,
      sku: item.sku,
      properties: item.properties || {},
      vendor: item.vendor,
      product_type: item.product_type
    })),
    attributes: cartResponse.attributes || {},
    note: cartResponse.note || null,
    customer: cartResponse.customer || null,
    requires_shipping: cartResponse.requires_shipping || false,
    taxes_included: cartResponse.taxes_included || false
  };
}

// ============================================================================
// CART EVENT HANDLERS
// ============================================================================

// Handle cart add events
async function handleCartAdd(cartData, variantId, quantity) {
  let config = window.DAWN_WEBHOOK_CONFIG || WEBHOOK_CONFIG;
  
  // Check if config exists and has the required structure
  if (!config || !config.events || !config.events.cart || !config.events.cart.add) {
    logWebhookEvent('config_missing', 'Webhook config missing or incomplete, using defaults');
    // Use default configuration if config is missing
    const defaultConfig = {
      enabled: true,
      debug: true,
      cartHoldExpiration: 30,
      events: {
        cart: {
          add: true,
          update: true,
          remove: true,
          clear: true,
          note: true
        }
      }
    };
    // Merge with available config
    config = { ...defaultConfig, ...config };
  }
  
  if (!config.events.cart.add) return;

  // Try to get current cart token if not available in cartData
  let cartToken = cartData.token || cartData.key;
  if (!cartToken) {
    try {
      // Use originalFetchGlobal to avoid interception loop
      const response = await originalFetchGlobal('/cart.js');
      const currentCart = await response.json();
      cartToken = currentCart.token;
      console.log('[Theme Webhook] Retrieved cart token from /cart.js:', cartToken);
    } catch (error) {
      console.warn('[Theme Webhook] Could not fetch current cart token:', error);
      cartToken = `cart-${Date.now()}`;
    }
  }

  // Handle different cart data structures
  let cartInfo;
  
  if (cartData.id && cartData.product_id) {
    // This is a single item response from cart/add
    cartInfo = {
      cart_token: cartToken,
      item_count: cartData.quantity || 1,
      total_price: cartData.line_price || cartData.price,
      items: [{
        id: cartData.id,
        variant_id: cartData.id,
        product_id: cartData.product_id,
        quantity: cartData.quantity,
        price: cartData.price,
        title: cartData.title,
        variant_title: cartData.variant_title,
        sku: cartData.sku,
        vendor: cartData.vendor,
        product_type: cartData.product_type
      }]
    };
  } else {
    // This is a full cart response
    cartInfo = extractCartData(cartData);
  }

  // Create standardized webhook data
  const standardizedData = createStandardizedWebhookData('carts/create', 'add_to_cart', {
    variant_id: variantId || cartData.id,
    quantity_added: quantity || cartData.quantity,
    cart: cartInfo
  }, {
    cart_hold_expiration: config.cartHoldExpiration
  });

  return await sendWebhookEvent('carts/create', standardizedData);
}

// Handle cart update events
async function handleCartUpdate(cartData, eventType = 'update') {
  console.log('[Theme Webhook] handleCartUpdate called with:', { cartData, eventType });
  
  // Don't send updates for empty carts - these should be handled as clear/remove events
  const itemCount = cartData?.item_count ?? cartData?.items?.length ?? 0;
  if (itemCount === 0) {
    logWebhookEvent('webhook_skipped_empty_cart_update', { eventType, reason: 'Cart is empty' });
    console.log('[Theme Webhook] Skipping empty cart update - cart has no items');
    return;
  }
  
  let config = window.DAWN_WEBHOOK_CONFIG || WEBHOOK_CONFIG;
  
  console.log('[Theme Webhook] Current config:', config);
  
  // Check if config exists and has the required structure
  if (!config || !config.events || !config.events.cart || !config.events.cart.update) {
    logWebhookEvent('config_missing', 'Webhook config missing or incomplete, using defaults');
    // Use default configuration if config is missing
    const defaultConfig = {
      enabled: true,
      debug: true,
      events: {
        cart: {
          add: true,
          update: true,
          remove: true,
          clear: true,
          note: true
        }
      }
    };
    config = { ...defaultConfig, ...config };
    console.log('[Theme Webhook] Using default config:', config);
  }
  
  if (!config.events.cart.update) {
    console.log('[Theme Webhook] Cart update disabled in config');
    return;
  }

  // Ensure cart data has a token - fetch current cart if needed
  let cartDataWithToken = cartData;
  if (!cartData.token && !cartData.key) {
    try {
      // Use originalFetchGlobal to avoid interception loop
      const response = await originalFetchGlobal('/cart.js');
      const currentCart = await response.json();
      cartDataWithToken = { ...cartData, token: currentCart.token };
      console.log('[Theme Webhook] Retrieved cart token for update from /cart.js:', currentCart.token);
    } catch (error) {
      console.warn('[Theme Webhook] Could not fetch current cart token for update:', error);
    }
  }

  // Create standardized webhook data
  const standardizedData = createStandardizedWebhookData(`carts/${eventType}`, eventType, {
    cart: extractCartData(cartDataWithToken)
  });

  return await sendWebhookEvent(`carts/${eventType}`, standardizedData);
}

// Handle cart remove events
async function handleCartRemove(cartData, variantId) {
  console.log('[Theme Webhook] handleCartRemove called with:', { cartData, variantId });
  
  let config = window.DAWN_WEBHOOK_CONFIG || WEBHOOK_CONFIG;
  
  // Check if config exists and has the required structure
  if (!config || !config.events || !config.events.cart || !config.events.cart.remove) {
    logWebhookEvent('config_missing', 'Webhook config missing or incomplete, using defaults');
    // Use default configuration if config is missing
    const defaultConfig = {
      enabled: true,
      debug: true,
      events: {
        cart: {
          add: true,
          update: true,
          remove: true,
          clear: true,
          note: true
        }
      }
    };
    config = { ...defaultConfig, ...config };
  }
  
  if (!config.events.cart.remove) return;

  // Extract cart token for deduplication
  const cartToken = cartData.cart_token || cartData.token || cartData.key || null;
  
  // Check deduplication before sending
  if (!shouldSendWebhook('carts/remove', variantId, cartToken)) {
    logWebhookEvent('webhook_skipped_duplicate', { eventType: 'carts/remove', variantId, cartToken });
    console.log('[Theme Webhook] Remove webhook skipped - duplicate detected');
    return { success: false, message: 'Duplicate webhook skipped' };
  }

  // Create standardized webhook data
  const standardizedData = createStandardizedWebhookData('carts/remove', 'remove_from_cart', {
    variant_id: variantId,
    cart: extractCartData(cartData),
    removed_item: cartData.removed_item || null,
    is_last_item: cartData.item_count === 1,
    cart_cleared: cartData.item_count === 0
  });

  console.log('[Theme Webhook] Sending remove webhook with data:', standardizedData);
  return await sendWebhookEvent('carts/remove', standardizedData);
}

// Handle cart clear events
async function handleCartClear(cartData) {
  let config = window.DAWN_WEBHOOK_CONFIG || WEBHOOK_CONFIG;
  
  // Check if config exists and has the required structure
  if (!config || !config.events || !config.events.cart || !config.events.cart.clear) {
    logWebhookEvent('config_missing', 'Webhook config missing or incomplete, using defaults');
    // Use default configuration if config is missing
    const defaultConfig = {
      enabled: true,
      debug: true,
      events: {
        cart: {
          add: true,
          update: true,
          remove: true,
          clear: true,
          note: true
        }
      }
    };
    config = { ...defaultConfig, ...config };
  }
  
  if (!config.events.cart.clear) return;

  // Extract cart token for deduplication
  const cartToken = cartData.cart_token || cartData.token || cartData.key || null;
  
  // Check deduplication before sending
  if (!shouldSendWebhook('carts/clear', null, cartToken)) {
    logWebhookEvent('webhook_skipped_duplicate', { eventType: 'carts/clear', cartToken });
    console.log('[Theme Webhook] Clear webhook skipped - duplicate detected');
    return { success: false, message: 'Duplicate webhook skipped' };
  }

  // Create standardized webhook data
  const standardizedData = createStandardizedWebhookData('carts/update', 'clear_cart', {
    cart: extractCartData(cartData)
  });

  return await sendWebhookEvent('carts/update', standardizedData);
}

// ============================================================================
// ORDER EVENT HANDLERS
// ============================================================================

// Handle order creation (checkout completion)
async function handleOrderCreate(orderData) {
  const config = window.DAWN_WEBHOOK_CONFIG || WEBHOOK_CONFIG;
  
  if (!config.events || !config.events.order || !config.events.order.create) {
    console.log('[Theme Webhook] Order create event disabled in config');
    return;
  }

  // Extract cart token for deduplication
  const orderId = orderData.id || orderData.order_number || null;
  const cartToken = orderData.cart_token || null;
  
  // Check deduplication before sending
  if (!shouldSendWebhook('orders/create', orderId, cartToken)) {
    logWebhookEvent('webhook_skipped_duplicate', { eventType: 'orders/create', orderId });
    console.log('[Theme Webhook] Order create webhook skipped - duplicate detected');
    return { success: false, message: 'Duplicate webhook skipped' };
  }

  const eventData = {
    action: 'order_created',
    order: {
      id: orderData.id,
      order_number: orderData.order_number,
      total_price: orderData.total_price,
      currency: orderData.currency,
      customer: orderData.customer,
      line_items: orderData.line_items || [],
      shipping_address: orderData.shipping_address,
      billing_address: orderData.billing_address,
      financial_status: orderData.financial_status,
      fulfillment_status: orderData.fulfillment_status,
      created_at: orderData.created_at
    }
  };

  console.log('[Theme Webhook] Sending order create webhook:', eventData);
  return await sendWebhookEvent('orders/create', eventData);
}

// ============================================================================
// WEBHOOK EVENT INTERCEPTORS
// ============================================================================

// Intercept XMLHttpRequest for cart requests
function interceptXHR() {
  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;
  
  XMLHttpRequest.prototype.open = function(method, url, ...rest) {
    this._interceptedUrl = url;
    this._interceptedMethod = method;
    return originalOpen.call(this, method, url, ...rest);
  };
  
  XMLHttpRequest.prototype.send = function(...args) {
    const url = this._interceptedUrl || '';
    const method = this._interceptedMethod || 'GET';
    
    // Check if this is a cart-related request
    if (url.includes('/cart/add') || url.includes('/cart/change') || url.includes('/cart/update')) {
      logWebhookEvent('xhr_cart_request', { url, method });
      
      // Listen for completion
      this.addEventListener('load', async function() {
        if (this.status >= 200 && this.status < 300) {
          logWebhookEvent('xhr_cart_success', { url, status: this.status });
          
          // Wait a bit for cart to update, then fetch current cart state
          setTimeout(async () => {
            try {
              const response = await originalFetchGlobal('/cart.js');
              if (response.ok) {
                const cartData = await response.json();
                
                // Determine event type based on URL
                let eventType = 'update';
                let action = 'update';
                if (url.includes('/cart/add')) {
                  eventType = 'create';
                  action = 'add_to_cart';
                }
                
                logWebhookEvent('xhr_cart_complete', { url, cartData, eventType });
                await sendCartWebhookEvent(cartData, eventType, action, null);
              }
            } catch (error) {
              logWebhookEvent('xhr_cart_fetch_error', error);
            }
          }, 200); // Wait 200ms for cart update to complete
        }
      });
    }
    
    return originalSend.apply(this, args);
  };
  
  logWebhookEvent('xhr_interceptor_setup', 'XMLHttpRequest interceptor configured');
}

// Intercept jQuery AJAX requests (for themes using jQuery like Empire)
function interceptJQueryAjax() {
  // Wait for jQuery to be available
  const setupJQueryInterceptor = () => {
    if (typeof jQuery === 'undefined' || typeof $ === 'undefined') {
      logWebhookEvent('jquery_not_ready', 'jQuery not available yet, will retry');
      setTimeout(setupJQueryInterceptor, 100);
      return;
    }
    
    logWebhookEvent('jquery_interceptor_setup', 'Setting up jQuery AJAX interceptor');
    
    // Intercept jQuery AJAX requests
    $(document).ajaxComplete(function(event, xhr, settings) {
      const url = settings.url || '';
      
      // Check if this is a cart-related request
      if (url.includes('/cart/add') || url.includes('/cart/change') || url.includes('/cart/update')) {
        logWebhookEvent('jquery_ajax_cart_request', { url, method: settings.type });
        
        // Wait a bit for cart to update, then fetch current cart state
        setTimeout(async () => {
          try {
            const response = await originalFetchGlobal('/cart.js');
            if (response.ok) {
              const cartData = await response.json();
              
              // Determine event type based on URL
              let eventType = 'update';
              let action = 'update';
              if (url.includes('/cart/add')) {
                eventType = 'create';
                action = 'add_to_cart';
              }
              
              logWebhookEvent('jquery_ajax_cart_complete', { url, cartData, eventType });
              await sendCartWebhookEvent(cartData, eventType, action, null);
            }
          } catch (error) {
            logWebhookEvent('jquery_ajax_cart_fetch_error', error);
          }
        }, 200); // Wait 200ms for cart update to complete
      }
    });
    
    logWebhookEvent('jquery_interceptor_ready', 'jQuery AJAX interceptor configured');
  };
  
  setupJQueryInterceptor();
}

// Intercept cart update events from existing theme components
function interceptCartEvents() {
  logWebhookEvent('intercept_setup', 'Setting up cart event interception');
  
  // Method 1: Intercept fetch requests to cart endpoints
  interceptCartFetchRequests();
  
  // Method 2: Listen for custom events
  interceptCustomEvents();
  
  // Method 3: Intercept form submissions to cart/add
  interceptCartFormSubmissions();
  
  // Method 4: Intercept jQuery AJAX requests (for Empire theme)
  interceptJQueryAjax();
  
  // Method 5: Intercept XMLHttpRequest (XHR) for cart requests
  interceptXHR();
  
  // Method 6: Monitor cart state changes via polling (fallback)
  interceptCartStateChanges();
}

// Intercept fetch requests to cart endpoints
function interceptCartFetchRequests() {
  // Use the original fetch that was saved at the top of the file
  const originalFetch = originalFetchGlobal;
  
  window.fetch = function(...args) {
    const [url, options] = args;
    
    // Check if this is a cart-related request
    if (typeof url === 'string' && url.includes('/cart/')) {
      logWebhookEvent('cart_fetch_intercepted', { url, method: options?.method || 'GET' });
      
      // Handle cart add requests (both /cart/add and /cart/add.js)
      if ((url.includes('/cart/add') || url.includes('/cart/add.js')) && options?.method === 'POST') {
        return originalFetch.apply(this, args).then(response => {
          if (response.ok) {
            // Try to parse as JSON first (for /cart/add.js)
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
            response.clone().json().then(cartData => {
              logWebhookEvent('cart_add_response', cartData);
                // Extract variant ID from request body if available
                let variantId = null;
                try {
                  const bodyText = options?.body;
                  if (bodyText) {
                    const body = typeof bodyText === 'string' ? JSON.parse(bodyText) : bodyText;
                    variantId = body.id || body.variant_id;
                  }
                } catch (e) {
                  // Ignore parse errors
                }
                sendCartWebhookEvent(cartData, 'create', 'add_to_cart', variantId);
            }).catch(err => {
              logWebhookEvent('cart_add_parse_error', err);
                // If JSON parse fails, fetch full cart instead
                originalFetchGlobal('/cart.js').then(cartResponse => cartResponse.json()).then(fullCart => {
                  logWebhookEvent('cart_add_fallback_to_full_cart', fullCart);
                  let variantId = null;
                  try {
                    const bodyText = options?.body;
                    if (bodyText) {
                      const body = typeof bodyText === 'string' ? JSON.parse(bodyText) : bodyText;
                      variantId = body.id || body.variant_id;
                    }
                  } catch (e) {
                    // Use first item's variant_id as fallback
                    variantId = fullCart.items?.[0]?.variant_id || null;
                  }
                  sendCartWebhookEvent(fullCart, 'create', 'add_to_cart', variantId);
                }).catch(fetchErr => {
                  logWebhookEvent('cart_add_fetch_fallback_error', fetchErr);
                });
              });
            } else {
              // If response is not JSON (redirect), fetch full cart instead
              logWebhookEvent('cart_add_non_json_response', { contentType, url });
              originalFetchGlobal('/cart.js').then(cartResponse => cartResponse.json()).then(fullCart => {
                logWebhookEvent('cart_add_fetched_full_cart', fullCart);
                let variantId = null;
                try {
                  const bodyText = options?.body;
                  if (bodyText) {
                    const body = typeof bodyText === 'string' ? JSON.parse(bodyText) : bodyText;
                    variantId = body.id || body.variant_id;
                  }
                } catch (e) {
                  // Use first item's variant_id as fallback
                  variantId = fullCart.items?.[0]?.variant_id || null;
                }
                sendCartWebhookEvent(fullCart, 'create', 'add_to_cart', variantId);
              }).catch(fetchErr => {
                logWebhookEvent('cart_add_fetch_error', fetchErr);
              });
            }
          }
          return response;
        });
      }
      
      // Handle cart change requests
      if (url.includes('/cart/change') && options?.method === 'POST') {
        // Capture cart state BEFORE the request is made
        return originalFetchGlobal('/cart.js').then(cartResponse => {
          return cartResponse.json().then(beforeCartData => {
            console.log('[Theme Webhook] Cart state before change:', beforeCartData);
            
            // Store the before state
            localStorage.setItem('cart_state_before_change', JSON.stringify(beforeCartData));
            
            // Now make the original request
            return originalFetch.apply(this, args).then(response => {
              if (response.ok) {
                response.clone().text().then(text => {
                  try {
                    const cartData = JSON.parse(text);
                    logWebhookEvent('cart_change_response', cartData);
                    
                    // Check if this is a remove operation (cart went from having items to empty)
                    if (cartData.item_count === 0 && beforeCartData.item_count > 0) {
                      console.log('[Theme Webhook] Detected remove operation (cart went from', beforeCartData.item_count, 'to', cartData.item_count, 'items)');
                      logWebhookEvent('detected_remove_operation', { previous: beforeCartData, current: cartData });
                      
                      // Send remove webhook with current empty cart state but include removed item details
                      const removeCartData = {
                        ...cartData, // Use current empty cart state
                        // Include the removed item details from before state
                        removed_item: beforeCartData.items[beforeCartData.items.length - 1],
                        // Include the before cart state for reference
                        before_cart_state: beforeCartData
                      };
                      
                      console.log('[Theme Webhook] Remove cart data:', removeCartData);
                      sendCartWebhookEvent(removeCartData, 'remove', 'remove_from_cart', 
                        beforeCartData.items[beforeCartData.items.length - 1]?.variant_id);
                      return;
                    }
                    
                    // Store current cart state for next comparison
                    localStorage.setItem('previous_cart_state', JSON.stringify(cartData));
                    
                    // Default to update webhook
                    sendCartWebhookEvent(cartData, 'update', 'update', null);
                  } catch (err) {
                    logWebhookEvent('cart_change_parse_error', err);
                  }
                }).catch(err => {
                  logWebhookEvent('cart_change_parse_error', err);
                });
              }
              return response;
            });
          });
        }).catch(error => {
          console.error('[Theme Webhook] Error getting cart state before change:', error);
          // Fallback to original behavior
          return originalFetch.apply(this, args);
        });
      }
      
      // Handle cart update requests
      if (url.includes('/cart/update') && options?.method === 'POST') {
        return originalFetch.apply(this, args).then(response => {
          if (response.ok) {
            response.clone().json().then(cartData => {
              logWebhookEvent('cart_update_response', cartData);
              sendCartWebhookEvent(cartData, 'update', 'update', null);
            }).catch(err => {
              logWebhookEvent('cart_update_parse_error', err);
            });
          }
          return response;
        });
      }
    }
    
    return originalFetch.apply(this, args);
  };
  
  logWebhookEvent('fetch_interceptor_setup', 'Fetch interceptor configured');
}

// Intercept custom events
function interceptCustomEvents() {
  // Listen for custom cart events
  document.addEventListener('cart:updated', (event) => {
    logWebhookEvent('custom_cart_event', event.detail);
    if (event.detail) {
      sendCartWebhookEvent(event.detail, 'update', 'update', null);
    }
  });
  
  document.addEventListener('cart:item-added', (event) => {
    logWebhookEvent('custom_cart_add_event', event.detail);
    if (event.detail) {
      sendCartWebhookEvent(event.detail, 'create', 'add_to_cart', event.detail.variant_id);
    }
  });
  
  document.addEventListener('cart:item-removed', (event) => {
    logWebhookEvent('custom_cart_remove_event', event.detail);
    if (event.detail) {
      sendCartWebhookEvent(event.detail, 'update', 'remove_from_cart', event.detail.variant_id);
    }
  });
}

// Intercept form submissions to cart/add
function interceptCartFormSubmissions() {
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!form || form.tagName !== 'FORM') return;
    
    const formAction = form.action || form.getAttribute('action') || '';
    const formMethod = (form.method || 'GET').toUpperCase();
    
    // Check if this is a cart/add form
    if (formAction.includes('/cart/add') && formMethod === 'POST') {
      // Prevent default form submission to avoid page redirect
      event.preventDefault();
      event.stopPropagation();
      
      logWebhookEvent('cart_form_submission_intercepted', { action: formAction, method: formMethod });
      
      // Get variant ID from form data
      const formData = new FormData(form);
      const variantId = formData.get('id') || formData.get('variant_id');
      const quantity = parseInt(formData.get('quantity')) || 1;
      
      logWebhookEvent('cart_form_variant_data', { variantId, quantity });
      
      try {
        // Submit to /cart/add.js instead (returns JSON, doesn't redirect)
        const addUrl = formAction.includes('.js') ? formAction : formAction.replace('/cart/add', '/cart/add.js');
        
        logWebhookEvent('cart_form_submitting_via_fetch', { url: addUrl, variantId, quantity });
        
        // Submit form via fetch to avoid page redirect
        const response = await originalFetchGlobal(addUrl, {
          method: 'POST',
          body: formData,
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          }
        });
        
    if (response.ok) {
      const cartData = await response.json();
          logWebhookEvent('cart_form_add_success', cartData);
          
          // Send webhook with full cart data
          await sendCartWebhookEvent(cartData, 'create', 'add_to_cart', variantId);
          
          logWebhookEvent('cart_form_webhook_complete', 'Webhook sent successfully');
          
          // Try multiple methods to trigger cart UI update
          let cartUIOpened = false;
          
          // Method 1: Try theme cart drawer
          if (typeof window.theme !== 'undefined') {
            if (window.theme.cartDrawer && typeof window.theme.cartDrawer.open === 'function') {
              window.theme.cartDrawer.open();
              cartUIOpened = true;
              logWebhookEvent('cart_form_opened_drawer', 'Opened theme cart drawer');
            }
          }
          
          // Method 2: Try jQuery cart drawer/notification (common in Shopify themes)
          if (!cartUIOpened && typeof jQuery !== 'undefined') {
            // Try common cart drawer methods
            if (typeof jQuery.fn.drawer === 'function') {
              jQuery('#CartDrawer').drawer('open');
              cartUIOpened = true;
            } else if (typeof jQuery.fn.modal === 'function') {
              jQuery('#CartDrawer').modal('show');
              cartUIOpened = true;
            }
            
            // Trigger cart update events
            jQuery(document.body).trigger('cart:updated', cartData);
            jQuery(document.body).trigger('ajaxCart:updated', cartData);
          }
          
          // Method 3: Dispatch custom events
          if (!cartUIOpened && typeof document.dispatchEvent === 'function') {
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: cartData }));
            document.dispatchEvent(new CustomEvent('ajaxCart:updated', { detail: cartData }));
            document.dispatchEvent(new Event('cart:refresh'));
          }
          
          // Method 4: Try to open cart drawer via data attributes (Shopify Dawn pattern)
          const cartDrawerButton = document.querySelector('[data-cart-drawer-toggle]');
          if (!cartUIOpened && cartDrawerButton) {
            cartDrawerButton.click();
            cartUIOpened = true;
            logWebhookEvent('cart_form_clicked_drawer_button', 'Clicked cart drawer toggle');
          }
          
          // Method 5: Redirect to cart page if no UI was triggered
          if (!cartUIOpened) {
            logWebhookEvent('cart_form_redirecting', 'No cart UI found, redirecting to cart page');
            // Small delay to ensure webhook completes
            setTimeout(() => {
              window.location.href = '/cart';
            }, 100);
          } else {
            logWebhookEvent('cart_form_complete', 'Cart add, webhook, and UI update completed');
          }
        } else {
          logWebhookEvent('cart_form_add_failed', { status: response.status });
          // Allow form to submit normally if fetch fails
          form.submit();
        }
      } catch (error) {
        logWebhookEvent('cart_form_error', error);
        // Allow form to submit normally on error
        form.submit();
      }
    }
    // For non-cart/add forms, allow normal submission (no intercept)
  });
}

// Check current cart state (don't send webhooks from here - only return cart data)
async function checkCartState() {
  return new Promise((resolve) => {
    try {
      // Use XMLHttpRequest instead of fetch for reliability
      const xhr = new XMLHttpRequest();
      xhr.open('GET', '/cart.js', true);
      xhr.onload = function() {
        if (xhr.status === 200) {
          try {
            const cartData = JSON.parse(xhr.responseText);
      logWebhookEvent('cart_state_check', cartData);
            // Don't send webhooks from state check - only return data
            // Webhooks should only be sent from explicit user actions (add, remove, etc.)
            resolve(cartData);
          } catch (e) {
            logWebhookEvent('cart_state_check_parse_error', e);
            resolve(null);
          }
        } else {
          resolve(null);
        }
      };
      xhr.onerror = function() {
        logWebhookEvent('cart_state_check_error', 'XHR failed');
        resolve(null);
      };
      xhr.send();
  } catch (error) {
    logWebhookEvent('cart_state_check_error', error);
      resolve(null);
    }
  });
}

// Monitor cart state changes via polling (fallback for when fetch interception misses events)
// Only use this as a last resort - prefer fetch/form interception
function interceptCartStateChanges() {
  let lastCartState = null;
  let lastCartItemCount = null;
  let initialized = false;
  let lastWebhookTime = 0; // Track when we last sent a webhook
  const POLL_BLOCK_WINDOW = 3000; // Don't send webhooks via polling if we sent one in last 3 seconds
  
  // Poll cart state every 500ms to catch changes
  setInterval(() => {
    // Skip polling if we recently sent a webhook (likely from form/fetch interception)
    if (Date.now() - lastWebhookTime < POLL_BLOCK_WINDOW) {
      return;
    }
    // Use XMLHttpRequest instead of fetch for reliability
    const xhr = new XMLHttpRequest();
    xhr.open('GET', '/cart.js', true);
    xhr.onload = function() {
      if (xhr.status !== 200) return;
      
      try {
        const currentCart = JSON.parse(xhr.responseText);
        const currentItemCount = currentCart.item_count || 0;
      
      // Initialize on first run - don't send webhook for existing cart items
      if (!initialized) {
        lastCartState = currentCart;
        lastCartItemCount = currentItemCount;
        initialized = true;
        return;
      }
      
      // Detect item count increase (add to cart)
      if (lastCartItemCount !== null && currentItemCount > lastCartItemCount) {
        const itemCountDiff = currentItemCount - lastCartItemCount;
        logWebhookEvent('cart_state_poll_detected_add', {
          previous: lastCartItemCount,
          current: currentItemCount,
          diff: itemCountDiff,
          cart: currentCart
        });
        
        // If we have previous state, determine what was added
        if (lastCartState && lastCartState.items && currentCart.items) {
          // Find the newly added item(s)
          const previousVariantIds = new Set(lastCartState.items.map(item => item.variant_id));
          const newItems = currentCart.items.filter(item => !previousVariantIds.has(item.variant_id));
          
          if (newItems.length > 0) {
            // Send add webhook for each new item - but use the full cart for context
            const newItem = newItems[0]; // Usually only one item added at a time
            console.log('[Theme Webhook] Polling detected cart add, sending webhook for variant:', newItem.variant_id);
            lastWebhookTime = Date.now(); // Mark that we're sending a webhook
            sendCartWebhookEvent(currentCart, 'create', 'add_to_cart', newItem.variant_id);
          } else {
            // Items might have been updated instead of added, send add webhook with current cart
            const lastItem = currentCart.items[currentCart.items.length - 1];
            console.log('[Theme Webhook] Polling detected cart add (no new variants), sending webhook for variant:', lastItem?.variant_id);
            lastWebhookTime = Date.now();
            sendCartWebhookEvent(currentCart, 'create', 'add_to_cart', lastItem?.variant_id);
          }
        } else {
          // No previous state, send add webhook with current cart
          const firstItem = currentCart.items?.[0];
          console.log('[Theme Webhook] Polling detected cart add (no previous state), sending webhook for variant:', firstItem?.variant_id);
          lastWebhookTime = Date.now();
          sendCartWebhookEvent(currentCart, 'create', 'add_to_cart', firstItem?.variant_id);
        }
      }
      
      // Update tracking state
      lastCartState = currentCart;
      lastCartItemCount = currentItemCount;
      } catch (error) {
        // Silently fail - polling shouldn't break functionality
        logWebhookEvent('cart_state_poll_error', error);
      }
    };
    xhr.onerror = function() {
      // Silently fail - polling shouldn't break functionality
      logWebhookEvent('cart_state_poll_xhr_error', 'Failed to fetch /cart.js');
    };
    xhr.send();
  }, 500); // Poll every 500ms
}

// Send webhook event for cart changes
async function sendCartWebhookEvent(cartData, eventType, action, variantId) {
  try {
    // Extract cart token for deduplication
    const cartToken = cartData.token || cartData.key || cartData.cart_token || null;
    
    // Check deduplication before sending
    if (!shouldSendWebhook(eventType, variantId, cartToken)) {
      logWebhookEvent('webhook_skipped_duplicate', { eventType, action, variantId });
      return;
    }
    
    logWebhookEvent('sending_webhook', { eventType, action, variantId, cartData });
    
    if (action === 'add_to_cart') {
      // Extract variant ID from cart data if not provided
      if (!variantId && cartData.id) {
        variantId = cartData.id;
      }
      await handleCartAdd(cartData, variantId, cartData.quantity || 1);
    } else if (action === 'clear_cart') {
      await handleCartClear(cartData);
    } else if (action === 'remove_from_cart') {
      console.log('[Theme Webhook] Handling remove_from_cart action:', { cartData, variantId });
      await handleCartRemove(cartData, variantId);
    } else if (action === 'update') {
      // Only send update if cart has items (don't send empty cart updates)
      if (!cartData || cartData.item_count === 0) {
        logWebhookEvent('webhook_skipped_empty_cart', { eventType, action });
        return;
      }
      await handleCartUpdate(cartData, eventType);
    } else if (action === 'state_check') {
      // Don't send webhooks for state checks - they're just internal checks
      logWebhookEvent('webhook_skipped_state_check', { eventType, action });
      return;
    }
  } catch (error) {
    logWebhookEvent('cart_webhook_error', error);
  }
}

// Create standardized webhook data format
function createStandardizedWebhookData(eventType, action, data, additionalData = {}) {
  const config = window.DAWN_WEBHOOK_CONFIG || WEBHOOK_CONFIG;
  
  // Ensure action is set correctly (override any incorrect action in data)
  const cleanData = { ...data };
  // Remove any action field from data to ensure we use the correct one
  delete cleanData.action;
  
  return {
    event_type: eventType,
    shop: config.shop || 'unknown-shop.myshopify.com',
    timestamp: new Date().toISOString(),
    source: 'custom_theme',
    theme: 'custom',
    data: {
      action: action, // Always set action explicitly
      ...cleanData
    },
    ...additionalData
  };
}

// ============================================================================
// INITIALIZATION
// ============================================================================

// Initialize webhook interceptor when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  console.log('[Theme Webhook] DOM loaded, initializing webhook interceptor');
  console.log('[Theme Webhook] window.DAWN_WEBHOOK_CONFIG:', window.DAWN_WEBHOOK_CONFIG);
  console.log('[Theme Webhook] window.CART_WEBHOOK_CONFIG:', window.CART_WEBHOOK_CONFIG);
  console.log('[Theme Webhook] Local WEBHOOK_CONFIG:', WEBHOOK_CONFIG);
  
  logWebhookEvent('init', 'Theme webhook interceptor initialized');
  
  // Wait a bit for configuration to load
  setTimeout(() => {
    // Check if configuration is available
    if (window.DAWN_WEBHOOK_CONFIG) {
      logWebhookEvent('config_loaded', 'Webhook configuration loaded successfully');
    } else {
      logWebhookEvent('config_missing', 'Webhook configuration not found, using defaults');
    }
    
    // Intercept cart events
    interceptCartEvents();
    
    // Send initial cart state if available
    if (window.cart && window.cart.item_count > 0) {
      handleCartUpdate(window.cart, 'page_load');
    }
    
    // Detect checkout completion (thank-you page)
    detectCheckoutCompletion();
  }, 100); // Small delay to ensure config is loaded
});

// ============================================================================
// CHECKOUT COMPLETION DETECTION
// ============================================================================

/**
 * Detect when user reaches thank-you page after checkout completion
 * This helps verify that checkout was successful and logs order information
 */
function detectCheckoutCompletion() {
  const config = window.DAWN_WEBHOOK_CONFIG || WEBHOOK_CONFIG;
  
  // Check if we're on a thank-you page
  const isThankYouPage = window.location.pathname.includes('/thank_you') || 
                         window.location.pathname.includes('/checkouts/') && 
                         window.location.pathname.includes('/thank_you') ||
                         document.querySelector('[data-order-confirmation]') ||
                         document.querySelector('.order-confirmation') ||
                         document.querySelector('[data-checkout-success]');
  
  if (isThankYouPage) {
    logWebhookEvent('checkout_completion_detected', {
      url: window.location.href,
      timestamp: new Date().toISOString()
    });
    console.log('[Theme Webhook] Checkout completion detected on thank-you page');
    
    // Try to extract order information from the page
    extractOrderInfoFromThankYouPage();
    
    // Note: Actual order webhook will be sent by Shopify to webhook_handler.php
    // This is just for client-side logging/verification
    if (config.debug) {
      console.log('[Theme Webhook] Note: Order webhook should be sent by Shopify to:', 
                  (window.location.origin.includes('shopify') ? 'webhook_handler.php' : 'Server-side webhook handler'));
    }
  }
}

/**
 * Extract order information from thank-you page if available
 */
function extractOrderInfoFromThankYouPage() {
  try {
    const orderInfo = {};
    
    // Try to get order number from various sources
    const orderNumberElement = document.querySelector('[data-order-number]') ||
                              document.querySelector('.order-number') ||
                              document.querySelector('[data-order-id]');
    
    if (orderNumberElement) {
      orderInfo.order_number = orderNumberElement.textContent.trim() || 
                               orderNumberElement.getAttribute('data-order-number') ||
                               orderNumberElement.getAttribute('data-order-id');
    }
    
    // Try to get order total
    const orderTotalElement = document.querySelector('[data-order-total]') ||
                              document.querySelector('.order-total') ||
                              document.querySelector('.total-price');
    
    if (orderTotalElement) {
      orderInfo.total_price = orderTotalElement.textContent.trim() || 
                              orderTotalElement.getAttribute('data-order-total');
    }
    
    // Check for order confirmation in URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('order')) {
      orderInfo.order_id = urlParams.get('order');
    }
    
    // Check for order data in window object (some themes expose this)
    if (window.order || window.checkout || window.orderConfirmation) {
      orderInfo.window_data = window.order || window.checkout || window.orderConfirmation;
    }
    
    // Check for order data in meta tags
    const orderMeta = document.querySelector('meta[name="order-number"]') ||
                      document.querySelector('meta[property="order:number"]');
    if (orderMeta) {
      orderInfo.order_number = orderMeta.getAttribute('content');
    }
    
    if (Object.keys(orderInfo).length > 0) {
      logWebhookEvent('order_info_extracted', orderInfo);
      console.log('[Theme Webhook] Extracted order info from thank-you page:', orderInfo);
    } else {
      console.log('[Theme Webhook] Could not extract order info from thank-you page');
    }
    
    return orderInfo;
  } catch (error) {
    console.error('[Theme Webhook] Error extracting order info:', error);
    return null;
  }
}

// Export for external use
window.DawnWebhookInterceptor = {
  sendWebhookEvent,
  handleCartAdd,
  handleCartUpdate,
  handleCartRemove,
  handleCartClear,
  handleOrderCreate,
  interceptCartEvents,
  get config() {
    return window.DAWN_WEBHOOK_CONFIG || WEBHOOK_CONFIG;
  }
};

